import { datadogRum, RumInitConfiguration } from '@datadog/browser-rum';
import { env } from './env';

// Route paths that embed conversation/agent IDs. Collapse them in RUM URLs
// so high-cardinality IDs do not leak as plaintext into Datadog. Restricted
// to URL-safe ID characters so the regex stops at spaces/punctuation when
// scrubbing free-form strings like error messages.
const AGENT_CHAT_PATH_RE = /\/a\/[A-Za-z0-9_-]+\/c\/[A-Za-z0-9_-]+/g;
const CHAT_PATH_RE = /\/c\/[A-Za-z0-9_-]+/g;
// Query params that may carry sensitive conversation content (chat stream
// endpoint puts the user message in the URL querystring for EventSource).
const QUERY_REDACT_KEYS = ['message', 'conversation_id'];

export function scrubUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  let out = url
    .replace(AGENT_CHAT_PATH_RE, '/a/:agentId/c/:chatId')
    .replace(CHAT_PATH_RE, '/c/:chatId');
  try {
    const hasOrigin = /^https?:\/\//.test(out);
    const u = new URL(out, window.location.origin);
    let mutated = false;
    for (const key of QUERY_REDACT_KEYS) {
      if (u.searchParams.has(key)) {
        u.searchParams.set(key, '[REDACTED]');
        mutated = true;
      }
    }
    if (mutated) {
      const rebuilt = u.pathname + (u.search || '') + (u.hash || '');
      out = hasOrigin ? u.origin + rebuilt : rebuilt;
    }
  } catch {
    // Non-URL strings (e.g. error messages with embedded paths): regex-only
    // pass above is sufficient.
  }
  return out;
}

// Interface for user information
interface UserInfo {
  id: string;
  user?: string;
  email?: string;
  sessionId?: string;
}

// Interface for Datadog InternalContext
interface InternalContext {
  application_id: string;
  session_id: string | undefined;
  view?: {
    id: string;
    url: string;
    referrer: string;
    name?: string;
  };
  user_action?: {
    id: string | string[];
  };
}

// Extended RUM type
interface DatadogRumExtended {
  getInternalContext?: () => InternalContext;
  [key: string]: any;
}

let sessionReplayTimeout: number | null = null;
let hasStartedSessionReplay = false;

// Initialize the Datadog RUM SDK
export const initDatadog = (userInfo?: UserInfo): void => {
  const releaseTag = env.DATADOG_RELEASE_TAG;
  if (!releaseTag) {
    console.warn('Datadog RUM initialization skipped: missing release tag');
    return;
  }
  // Only initialize if we have application ID and client token
  const applicationId = env.DATADOG_APPLICATION_ID;
  const clientToken = env.DATADOG_CLIENT_TOKEN;

  if (!applicationId || !clientToken) {
    console.warn('Datadog RUM initialization skipped: missing application ID or client token');
    return;
  }

  try {

    const allowedTracingUrls = env.DATADOG_TRACING_URLS
      .split(',')
      .map(url => url.trim())
      .filter(Boolean);

    const config: RumInitConfiguration = {
      applicationId,
      clientToken,
      site: 'datadoghq.eu',
      service: env.DATADOG_SERVICE_NAME,
      env: env.BUILD_ENV,
      version: env.VERSION || '1.0.0',
      allowedTracingUrls,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      // 'mask-user-input' masks form inputs (textareas, contenteditable) so
      // typed-but-unsent messages are not captured in replay. Rendered chat
      // bubbles + sidebar titles are masked separately via `data-dd-privacy`.
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: (event) => {
        if (event.type === 'view') {
          event.view.url = scrubUrl(event.view.url) ?? event.view.url;
          event.view.referrer = scrubUrl(event.view.referrer);
        } else if (event.type === 'resource') {
          event.resource.url = scrubUrl(event.resource.url) ?? event.resource.url;
        } else if (event.type === 'error') {
          if (event.error.message) {
            event.error.message = scrubUrl(event.error.message) ?? event.error.message;
          }
          if (event.error.stack) {
            event.error.stack = scrubUrl(event.error.stack) ?? event.error.stack;
          }
          if (event.error.resource?.url) {
            event.error.resource.url = scrubUrl(event.error.resource.url) ?? event.error.resource.url;
          }
        }
        return true;
      },
    };

    datadogRum.init(config);
    datadogRum.setGlobalContextProperty('release_tag', releaseTag);

    // Set user information if available
    if (userInfo) {
      const userData: Record<string, any> = {
        id: userInfo.id,
      };

      if (userInfo.email) userData.email = userInfo.email;
      if (userInfo.user) userData.user = userInfo.user;

      // Apply the session ID if provided
      if (userInfo.sessionId) {
        // Set the session ID in the RUM context
        datadogRum.setGlobalContextProperty('session_id', userInfo.sessionId);
      }

      datadogRum.setUser({ id: userInfo.id, email: userInfo.email, user: userInfo.user, name: userInfo.user });
      console.log('Datadog RUM user set with for', userInfo.user);
    }

    // Enable session replay

    scheduleSessionReplayStart();
    console.log('Datadog RUM initialized');

  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
  }
};

// Export a function to manually start recording
const startSessionReplay = () => {
  if (hasStartedSessionReplay) {
    return;
  }
  datadogRum.startSessionReplayRecording();
  hasStartedSessionReplay = true;
};

const scheduleSessionReplayStart = () => {
  if (hasStartedSessionReplay) {
    return;
  }

  if (sessionReplayTimeout !== null) {
    return;
  }

  if (typeof window === 'undefined') {
    startSessionReplay();
    return;
  }

  sessionReplayTimeout = window.setTimeout(() => {
    sessionReplayTimeout = null;
    startSessionReplay();
  }, 300);
};

// Export a function to manually stop recording
export const stopSessionRecording = (): void => {
  datadogRum.stopSessionReplayRecording();
};

// Export a function to set user information
export const setDatadogUser = (userInfo: UserInfo): void => {
  try {
    const userData: Record<string, any> = {
      id: userInfo.id,
    };

    if (userInfo.user) userData.user = userInfo.user;

    // Apply the session ID if provided
    if (userInfo.sessionId) {
      // Set the session ID in the RUM context
      datadogRum.setGlobalContextProperty('session_id', userInfo.sessionId);
    }

    datadogRum.setUser({ id: userInfo.id, email: userInfo.email, user: userInfo.user, name: userInfo.user });
  } catch (error) {
    console.error('Failed to set Datadog user information:', error);
  }
};

// Testing functions
export const isDatadogInitialized = (): boolean => {
  // Check if Datadog RUM is initialized by checking if internal context exists
  const ddRum = datadogRum as DatadogRumExtended;
  return !!ddRum.getInternalContext && !!ddRum.getInternalContext();
};

export const logTestEvent = (eventName: string, properties?: Record<string, any>): void => {
  try {
    datadogRum.addAction(eventName, properties || {});
    console.log(`Test event "${eventName}" logged to Datadog RUM`);
  } catch (error) {
    console.error(`Failed to log test event "${eventName}" to Datadog RUM:`, error);
  }
};

export const setConversationContext = (conversationId: string): void => {
  try {
    datadogRum.setGlobalContextProperty('conversation_id', conversationId);
  } catch (error) {
    console.error('Failed to set Datadog conversation_id:', error);
  }
};

export const clearConversationContext = (): void => {
  try {
    datadogRum.removeGlobalContextProperty('conversation_id');
  } catch (error) {
    console.error('Failed to clear Datadog conversation_id:', error);
  }
};

export const getCurrentSessionId = (): string | null => {
  try {
    const ddRum = datadogRum as DatadogRumExtended;
    if (ddRum.getInternalContext) {
      const context = ddRum.getInternalContext();
      return context?.session_id || null;
    }
    return null;
  } catch (error) {
    console.error('Failed to get current Datadog session ID:', error);
    return null;
  }
};
